
import { useEffect, useRef, useState } from 'react';

import useAxios from 'library/axios'; 
import  {IEmployeeDeduction, IEmployeeDeductionSearch} from '../models/EmployeeDeduction';
import { HeadCell, RowCheckedMode } from 'components/ui/EnhancedTable';

import { useTranslation  } from 'react-i18next';
import { IPagination, ITextFilterElement } from 'components/ui/BasicTextFilterForm';

import IEnumeration, { Enum_EMPLOYEE_DEDUCTION_TYPE, Enum_OPERATION_STATUS, IEnumerationItem} from 'features/configuration/models/Enumeration';
import useEnumerationService from 'features/configuration/services/Enumeration';
import { useQuery } from 'react-query';

import { addMonths } from 'date-fns';
import { useRecoilValue } from 'recoil';
import { currentUserSessionAtom } from 'library/store';


const _ = () => {

    const axios = useAxios();    

    const createEmployeeDeduction = async (employeeDeduction: IEmployeeDeduction)  =>       
        await (await axios.post('/api/production/employeeDeduction/create', employeeDeduction)).data;

    const updateEmployeeDeduction = async (employeeDeduction: IEmployeeDeduction)  =>       
        await (await axios.post('/api/production/employeeDeduction/update', employeeDeduction)).data;

    const getEmployeeDeduction = async (employeeDeductionId  : number )  => {        
        const {data} = (await axios.get(`/api/production/employeeDeduction/getEmployeeDeduction/${employeeDeductionId}`));
        return await data;
      }

    const getEmployeeDeductions = async (criteria  : IEmployeeDeductionSearch, pagination?: IPagination)  => {
        const { name,description} = criteria;

        const pageSize = pagination?.pageSize ?? 50;
        const pageNumber = pagination?.pageNumber ?? 1;

        const {data} = (await axios.get(`api/production/employeeDeduction/get-employeeDeductions?name=${name}&description=${description}&pageSize=${pageSize}&pageNumber=${pageNumber}`));
        return await data;
      }

    

      
    return {    
        createEmployeeDeduction,    
        updateEmployeeDeduction,    
        getEmployeeDeduction,

        getEmployeeDeductions,
      
    } 
}

export default _;


export interface IFilterEmployeeDeductionOption {
    rowCheckedMode: RowCheckedMode,
    stateSelected?: [string[], React.Dispatch<React.SetStateAction<string[]>>],
    stateFiltered?: [IEmployeeDeduction[], React.Dispatch<React.SetStateAction<IEmployeeDeduction[]>>],
  }
  
  const defaultFilterEmployeeDeductionOption: IFilterEmployeeDeductionOption = {
    rowCheckedMode: 'single'
    //stateSelected: navigate
  }
  
  
  export const useBasicFilterEmployeeDeduction = (  onRowDoubleClick: (event: React.MouseEvent<unknown>, row: IEmployeeDeduction) => void,
                                              filterOption?: IFilterEmployeeDeductionOption  ) => {
  
    const { getEmployeeDeductions } = _();

    const {getEnumerationItemsByEnumerationCodes, getAsOptions } = useEnumerationService();
  
    const { t, i18n } = useTranslation();   
    const {language: lg } = useRecoilValue(currentUserSessionAtom);

    const {rowCheckedMode, stateSelected, stateFiltered} = filterOption || defaultFilterEmployeeDeductionOption;
  
    const {data: enumItems, refetch: refetchEnumerationItems} = useQuery<IEnumerationItem[]>(
      ['EnumerationItems', 'AdvanceOnCommission', 'Service'], 
        () => getEnumerationItemsByEnumerationCodes( [ Enum_EMPLOYEE_DEDUCTION_TYPE, Enum_OPERATION_STATUS ] ));   
  
      const getAdvanceTypeList = (row: IEmployeeDeduction, cellId: keyof IEmployeeDeduction, 
        opts: {value: string, name: string}[]) => {
        return getAsOptions(refEnumItems.current ?? [],Enum_EMPLOYEE_DEDUCTION_TYPE);
      }

      const getStatusList = (row: IEmployeeDeduction, cellId: keyof IEmployeeDeduction, 
        opts: {value: string, name: string}[]) => {
        return getAsOptions(refEnumItems.current ?? [],Enum_OPERATION_STATUS);
      }
  
  
    const [headEmployeeDeductionCells, setHeadEmployeeDeductionCells]  = useState<HeadCell<IEmployeeDeduction>[]>([
      {id:'id', label : t('Id'),  display: true, type: 'numeric', },
      {id:'deductionType', label : t('Deduction type'),  display: true, type: 'string', getOptions: getAdvanceTypeList },     

      {id:'personFullNameFR', label : t('Name'),  display: lg.includes('fr'), type: 'string', },
      {id:'personFullNameEN', label : t('Name'),  display: lg.includes('en'), type: 'string', },

      {id:'description', label : t('Description'),  display: true, type: 'string', },    

      {id:'status', label : t('Status'),  display: true, type: 'string', getOptions: getStatusList },   
  
    ]); 

    const [filterElements,] = useState<ITextFilterElement[]>( [
      
        {name: 'dateFrom', text: t('Start date'), dataType: 'date' ,  value: '', dateValue: addMonths(new Date(), -1)},
        {name: 'dateTo', text: t('End date'), dataType: 'date'  , value: '', dateValue: new Date()},

        {name: 'type', text: `-${t('Type')}-`, value: '0' , dataType: 'enumeration', 
          options: [ 
                    {name: `${t('Current')} (${t('Validated')} / ${t('Not validated')})`, value: '00'},
                    {name: t('Base'), value: '1'},
                    {name: t('Advance on salary'), value: '2'},
                    //{name: t('Commission'), value: '3'},
                    {name: t('Policy'), value: '4'},
                    //{name: t('Advance'), value: '5'}, 
                    //{name: `${t('Policy')} - ${t('Retraction')}`, value: '5'}, 
                    ]
          },

        {name: 'deductionType', text: t('Deduction type'), value: '', dataType: 'enumeration', 
              options: [...getAsOptions(enumItems, Enum_EMPLOYEE_DEDUCTION_TYPE)] },
        {name: 'name', text: t('Name'), value: ''},
        {name: 'description', text: t('Description'), value: ''},
       
      ]);  
  
    const [filteredEmployeeDeductions, ] = useState<IEmployeeDeduction[]>([]); 
  
    const onFilterButtonClick = async (filterElements: ITextFilterElement[], pagination?: IPagination) : Promise<IEmployeeDeduction[]> => {
     
      const dateFrom = filterElements.find( elt => elt.name === 'dateFrom')?.dateValue || new Date();
      const dateTo = filterElements.find( elt => elt.name === 'dateTo')?.dateValue || new Date();

      const type = Number( filterElements.find( elt => elt.name === 'type')?.value ) || 0;

      const deductionType = filterElements.find( elt => elt.name === 'deductionType')?.value || '';
      const name = filterElements.find( elt => elt.name === 'name')?.value || '';
      const description = filterElements.find( elt => elt.name === 'description')?.value || '';
      
  
      const arr = await getEmployeeDeductions( {dateFrom, dateTo, type, deductionType, name,description}, pagination );
      
      return arr;
    }
  
    const objKey: keyof IEmployeeDeduction = 'id';

    const refEnumItems = useRef<IEnumerationItem[]>();
    useEffect( () => {
      refEnumItems.current = enumItems;
    }, [enumItems])
  
    return {
      title: `${t('Employee deduction')}(s)`, headCells: headEmployeeDeductionCells, objKey,
      filterElements, rows: filteredEmployeeDeductions, 
      onFilterButtonClick, onRowDoubleClick, rowCheckedMode, stateSelected, stateFiltered
    }
  }